import { API_BASE_URL } from '../config/constants';
import { auth } from '../config/firebase';

export interface ContentGenerationRequest {
  brandId: string;
  personaId?: string;
  platforms: string[];
  topic: string;
  shortDescription: string;
  tone: string;
  contentLength: 'short' | 'medium' | 'long';
  language: string;
  keywords: string[];
  sourceUrl?: string;
  format?: 'regular' | 'reel';
  generateImage?: boolean;
  imageStyle?: string;
  style_type?: string;
  aspect_ratio?: string;
  imageSize?: string;
}

export interface ContentGenerationResponse {
  content?: {
    feedback?: string;
    platforms?: Record<string, PlatformContent>;
  };
  imageUrl?: string | null;
  timestamp?: string;
  metadata?: {
    persona?: any;
    brand?: any;
    sourceAnalysis?: any;
  };
  success?: boolean;
  message?: string;
  data?: {
    contents: {
      platform: string;
      content: string | PlatformContent;
      hashtags?: string[];
    }[];
  };
  // New API response format fields
  generatedContents?: Array<{
    platform: string;
    generatedContent: string;
    contentScore?: number;
    imageUrl?: string;
    [key: string]: any;
  }>;
  brandId?: string;
  personaId?: string;
}

export interface PlatformContent {
  text?: string;
  image?: string;
  [key: string]: any;
}

// Define the main endpoint and fallbacks
const API_ENDPOINT = '/ai-coach/content/generate';
const FALLBACK_ENDPOINTS = [
  '/ai/content/generate', 
  '/api/content/generate'
];

// Create a sample response for debugging and fallback
const createDummyResponse = (request: ContentGenerationRequest): ContentGenerationResponse => {
  return {
    success: true,
    data: {
      contents: request.platforms.map(platform => ({
        platform,
        content: `Sample content for ${platform}. This is a fallback response for debugging.`,
        hashtags: ['#sample', '#debug']
      }))
    },
    timestamp: new Date().toISOString()
  };
};

/**
 * Generate content using the AI Coach API
 * @param data Content generation request data
 * @returns Promise with the generated content
 */
export const generateContent = async (data: ContentGenerationRequest): Promise<ContentGenerationResponse> => {
  console.log('Starting content generation with params:', data);
  
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      console.error('Authentication token not available');
      throw new Error('No authentication token available');
    }

    console.log(`Attempting to generate content using endpoint: ${API_BASE_URL}${API_ENDPOINT}`);
    
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    console.log(`Response status from ${API_ENDPOINT}:`, response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`API error from ${API_ENDPOINT}:`, response.status, errorText);
      // Attempt to parse errorText as JSON if possible, otherwise use raw text
      let errorDetails = errorText;
      try {
        const parsedError = JSON.parse(errorText);
        errorDetails = parsedError?.details || parsedError?.error || errorText;
      } catch (e) { /* Ignore parsing error, use raw text */ }
      
      throw new Error(`Failed to generate content: ${response.status} ${response.statusText}. Details: ${errorDetails}`);
    }

    const rawText = await response.text();
    console.log('Raw API response text length:', rawText.length);
    console.log('Contains escaped newlines:', rawText.includes('\\n'));

    // Attempt to parse JSON
    let responseData: ContentGenerationResponse;
    try {
       responseData = JSON.parse(rawText);
    } catch (parseError) {
        console.error('Failed to parse API response JSON:', parseError);
        console.error('Raw response text was:', rawText); // Log the raw text for debugging
        throw new Error('Failed to parse AI response. The format received from the server was invalid.');
    }

    console.log('Content generation API response:', responseData);
    
    // Return the parsed data (could still be an error response format from the backend)
    return responseData; 

  } catch (error) {
    console.error('Error during content generation request:', error);
    // Return a consistent error format
    return {
      success: false,
      message: error instanceof Error ? error.message : 'An unknown error occurred during the request',
      data: { contents: [] }, // Ensure data exists even on error
    };
  }
}; 